// Page-level glue of the match: creates the long-lived renderer / HUD / session on first use,
// starts and stops the session when `gameJoin` changes, mirrors settings into the engines and
// mounts the in-match overlay (pause menu).

import { effect, untracked } from "@preact/signals";
import { h, render } from "preact";
import { audio } from "../audio";
import { errorMessage } from "../api/client";
import { Hud } from "../hud";
import { Renderer } from "../render";
import { settings } from "../settings/store";
import { gameError, gameJoin, gameStatus } from "../state/game";
import { handleEscape, screen } from "../state/route";
import { GameOverlay } from "../ui/screens/Game";
import { installUiSounds } from "../ui/uiSounds";
import { gamePaused } from "./pause";
import { GameSession } from "./session";

let session: GameSession | null = null;

/** Creates the page-lifetime objects once. Throws when WebGL 2 is unavailable. */
function ensureSession(): GameSession {
  if (session) return session;
  const canvas = document.getElementById("game-canvas");
  const hudRoot = document.getElementById("hud-root");
  if (!(canvas instanceof HTMLCanvasElement) || !hudRoot) throw new Error("#game-canvas or #hud-root missing from index.html");
  const renderer = new Renderer(canvas, settings.value.video);
  const hud = new Hud(hudRoot);
  hud.setVisible(false);
  session = new GameSession(renderer, hud, audio, canvas);
  window.addEventListener("resize", () => renderer.resize());
  return session;
}

function fail(e: unknown): void {
  gameError.value = errorMessage(e);
  gameStatus.value = "error";
}

/** Runs `fn` when the browser has nothing better to do (the menus are up), at the latest after `timeoutMs`. */
function whenIdle(fn: () => void, timeoutMs: number): void {
  if (typeof requestIdleCallback === "function") requestIdleCallback(fn, { timeout: timeoutMs });
  else setTimeout(fn, timeoutMs);
}

/** Called once from main.tsx. */
export function installGameSession(): void {
  // Rendering the sound bank is a ~250 ms task: do it in the menus, not when the first match starts.
  whenIdle(() => void audio.init(), 3000);
  // Creating the renderer (WebGL context, environment map, first programs) is a ~200 ms task too.
  whenIdle(() => {
    try {
      ensureSession();
    } catch {
      // No WebGL 2: the first join reports it on the connection panel.
    }
  }, 3000);
  installUiSounds(audio);
  const overlay = document.createElement("div");
  overlay.id = "game-overlay";
  document.body.append(overlay);
  render(h(GameOverlay, null), overlay);

  // A new join (first match, or the error panel's Retry with a fresh ticket) starts a session; null tears it down.
  effect(() => {
    const join = gameJoin.value;
    untracked(() => {
      if (!join) {
        session?.leave();
        return;
      }
      try {
        void ensureSession()
          .start(join)
          .catch((e: unknown) => fail(e));
      } catch (e) {
        fail(e);
      }
    });
  });

  // Settings: volumes always, engines once they exist.
  effect(() => {
    const s = settings.value;
    audio.setVolumes({ master: s.audio.master, sfx: s.audio.sfx, ui: s.audio.ui, music: s.audio.music });
    untracked(() => session?.applySettings());
  });

  // While the pause menu is open over the match, the app's Escape handler is off (the match owns
  // Escape), so the settings screen opened from the menu needs its own "back".
  window.addEventListener("keydown", (e) => {
    if (e.key !== "Escape" || e.defaultPrevented || e.isComposing) return;
    if (gameStatus.value !== "playing" || !gamePaused.value || screen.value === "game") return;
    if (handleEscape()) e.preventDefault();
  });

  installBackGuard();
}

// ---- Browser Back and closing the tab ----
//
// The app never pushes history entries, so Back (mouse side button, Alt+Left) would leave the site
// and drop the connection mid-match. While a match is on, one entry of ours sits on top of the
// history: Back pops it, counts as Escape (pause menu, or one screen back), and the entry is
// pushed again. When the match is over the entry is removed.

let backGuard = false;
/** The next popstate comes from our own `history.back()`. */
let ownPop = false;

function pushBackGuard(): void {
  history.pushState({ gunrunMatch: true }, "");
  backGuard = true;
}

function onPopState(): void {
  if (ownPop) {
    ownPop = false;
    return;
  }
  if (!backGuard) return;
  backGuard = false; // Back popped it
  if (gameStatus.value === "playing") {
    if (!gamePaused.value) session?.pause();
    else if (screen.value !== "game") handleEscape();
  } else {
    handleEscape();
  }
  if (gameStatus.value !== "idle") pushBackGuard();
}

function installBackGuard(): void {
  effect(() => {
    const idle = gameStatus.value === "idle";
    untracked(() => {
      if (!idle && !backGuard) {
        pushBackGuard();
      } else if (idle && backGuard) {
        backGuard = false;
        ownPop = true;
        history.back();
      }
    });
  });
  window.addEventListener("popstate", onPopState);
  // Closing, reloading or navigating away mid-match asks first.
  window.addEventListener("beforeunload", (e) => {
    if (gameStatus.value !== "playing") return;
    e.preventDefault();
    e.returnValue = "";
  });
}
